import { Injectable, signal, computed, effect } from '@angular/core';
import { Location } from '@moon-v2/moon-share';
import { toObservable } from '@angular/core/rxjs-interop';

const STORAGE_KEY = 'moon.selectedCountry';

@Injectable({
  providedIn: 'root',
})
export class CountryService {
  // 1. État interne avec Signals (Privé)
  private _selectedCountry = signal<Location | null>(this.loadFromStorage());
  private _userConnectedLocations = signal<Location[] | null>(null);
  // Chaîne complète des ancêtres de la location propre de l'utilisateur, du pays (level 0,
  // premier élément) jusqu'à sa location exacte (dernier élément) — distincte de
  // `selectedCountry` qui reste toujours le pays. Pas de persistance localStorage : dérivée
  // du profil de l'utilisateur courant à chaque démarrage, elle ne doit jamais survivre à un
  // changement de compte dans le même navigateur.
  private _userLocationChain = signal<Location[]>([]);

  // 2. Interface publique (Lecture seule via Signals)
  public readonly selectedCountry = this._selectedCountry.asReadonly();
  public readonly countries = this._userConnectedLocations.asReadonly();

  /**
   * Chaîne d'ancêtres de la location propre de l'utilisateur — vide si son pays d'origine
   * (chain[0]) ne correspond plus au pays actuellement sélectionné. `setUserLocationChain`
   * n'est renseignée qu'une fois au démarrage (voir UserConnectedLocationService), donc sans
   * ce garde, changer de pays via le sélecteur laisserait `userOwnLocation`/
   * `effectiveInLocationUuid` continuer de pointer vers l'ancien pays.
   */
  public readonly userLocationChain = computed<Location[]>(() => {
    const chain = this._userLocationChain();
    return chain.length && chain[0]?.uuid === this._selectedCountry()?.uuid ? chain : [];
  });

  /** Location propre de l'utilisateur (dernier élément de `userLocationChain`). */
  public readonly userOwnLocation = computed<Location | null>(() => {
    const chain = this.userLocationChain();
    return chain.length ? chain[chain.length - 1] : null;
  });

  // 3. Compatibilité avec le code existant qui utilise des Observables
  public readonly selectedCountry$ = toObservable(this._selectedCountry);
  public readonly countries$ = toObservable(this._userConnectedLocations);

  /**
   * Pays entre lesquels l'agent peut choisir : la liste du backend, ou à défaut son seul pays.
   *
   * ⚠️ `countries` reste `null` pour un agent mono-pays : `app.ts` n'appelle `setCountries()` que
   * si l'API renvoie **plus d'un** pays (le sélecteur de la barre n'a rien à proposer sinon). Son
   * pays est alors seulement dans `selectedCountry`, résolu depuis son profil. Un écran qui
   * propose de choisir un pays lit donc ce `computed`, jamais `countries` seul : sinon l'agent
   * mono-pays se verrait proposer une liste vide.
   *
   * Le backend décide des pays renvoyés selon les droits du jeton : le front ne lit aucun rôle.
   */
  public readonly availableCountries = computed<Location[]>(() => {
    const countries = this._userConnectedLocations();
    if (countries?.length) return countries;
    const current = this._selectedCountry();
    return current ? [current] : [];
  });

  // 4. Computed pour vérifier si l'utilisateur a plusieurs pays
  public readonly hasMultipleCountries = computed(() => {
    const countries = this._userConnectedLocations();
    return countries ? countries.length > 1 : false;
  });

  /**
   * Scope effectif pour les requêtes de données (emplacements, carte...) : la location
   * propre de l'utilisateur si elle est sous le pays (level > 0), sinon le pays lui-même.
   * Les droits RBAC du backend sont bornés à cette location — l'utiliser au lieu du pays
   * évite les requêtes hors scope autorisé (404/403). Distinct de `selectedCountry`, qui
   * reste le pays pour la config admin (OptionsStore) et les catalogues pays (offres...).
   */
  public readonly effectiveInLocationUuid = computed<string | undefined>(() => {
    const own = this.userOwnLocation();
    if (own?.uuid && (own.level ?? 0) > 0) return own.uuid;
    return this._selectedCountry()?.uuid;
  });

  constructor() {
    // Effect pour sauvegarder automatiquement dans localStorage
    effect(() => {
      const country = this._selectedCountry();
      if (country) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(country));
        } catch (e) {
          console.warn('Erreur sauvegarde localStorage:', e);
        }
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    });
  }

  // --- Gestion du Pays Sélectionné ---

  /**
   * Définit le pays sélectionné
   */
  setCountry(country: Location | null): void {
    this._selectedCountry.set(country);
  }

  /**
   * Définit la chaîne d'ancêtres de la location propre de l'utilisateur (voir
   * `userLocationChain`/`userOwnLocation`/`effectiveInLocationUuid`).
   */
  setUserLocationChain(chain: Location[]): void {
    this._userLocationChain.set(chain);
  }

  /**
   * Réinitialise le pays sélectionné
   */
  clear(): void {
    this._selectedCountry.set(null);
  }

  // --- Gestion de la liste des pays ---

  /**
   * Définit la liste des pays disponibles pour l'utilisateur — déjà triée par nom
   * côté backend (`order[name]: asc` sur l'appel getAllDirect), aucun tri client requis.
   */
  setCountries(countries: Location[]): void {
    this._userConnectedLocations.set(countries);
  }

  // --- Logique Métier ---

  /**
   * Vérifie si l'UUID fourni est autorisé pour l'utilisateur
   */
  checkCountryAccess(countryUUID: string): boolean {
    const locations = this._userConnectedLocations();
    const currentCountry = this._selectedCountry();

    if (!locations) {
      console.warn('Liste des pays non chargée');
      return false;
    }

    // Si le pays actuel est déjà celui demandé
    if (currentCountry?.uuid === countryUUID) {
      return true;
    }

    const isTargetAuthorized = locations.some((c) => c.uuid === countryUUID);

    if (isTargetAuthorized) {
      // Le pays est autorisé mais différent de celui sélectionné
      console.log('Pays autorisé mais différent:', countryUUID);
      return true;
    } else {
      // Le pays n'est pas dans la liste autorisée
      console.warn('Pays non autorisé:', countryUUID);
      return false;
    }
  }

  /**
   * Trouve un pays par son UUID
   */
  findCountryByUuid(uuid: string): Location | undefined {
    const countries = this._userConnectedLocations();
    return countries?.find((c) => c.uuid === uuid);
  }

  // --- Méthodes privées (Helpers) ---

  private loadFromStorage(): Location | null {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;

    try {
      return JSON.parse(saved) as Location;
    } catch {
      console.warn('Impossible de parser le pays depuis le localStorage');
      return null;
    }
  }
}
